import { Button, Text } from "@mantine/core";
import { hideNotification, showNotification } from "@mantine/notifications";
import { IconClock, IconMail } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useUpcomingMeetingReminders from "hooks/useUpcomingMeetingReminders";
import RunningLateModal from "components/Workspace/MeetingList/RunningLateModal/RunningLateModal";
import useWorkspaceStore from "stores/useWorkspaceStore";
import {
  MEETING_REMINDER_THRESHOLDS,
  getReminderMeetingSelection,
} from "utils/meetingReminders";
import { playMeetingReminderSound } from "utils/notificationSound";

const AUTO_DISMISS_MS = 120 * 1000;

const getReminderTheme = (thresholdMinutes) => {
  const isUrgent =
    thresholdMinutes === MEETING_REMINDER_THRESHOLDS.URGENT_MINUTES;

  return {
    color: isUrgent ? "red" : "yellow",
    ribbonColor: isUrgent ? "#e03131" : "#fab005",
  };
};

const isNotificationActionClick = (event) =>
  event.target instanceof Element &&
  !!event.target.closest("button, a, [role='button']");

const MeetingReminderMessage = ({ reminder, onRunningLate }) => {
  const canSendRunningLate = reminder.event?.attendees?.length > 1;

  return (
    <>
      <Text size="sm" mb={canSendRunningLate ? 8 : 0}>
        {reminder.message}
      </Text>

      {canSendRunningLate && (
        <Button
          size="xs"
          compact
          leftIcon={<IconMail size={14} />}
          onClick={(event) => {
            event.stopPropagation();
            hideNotification(reminder.id);
            onRunningLate(reminder.event);
          }}
        >
          Send running late email
        </Button>
      )}
    </>
  );
};

const UpcomingMeetingReminderHost = ({ user, enabled = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { reminders, dismissReminder } = useUpcomingMeetingReminders({
    user,
    enabled,
  });
  const { setSelectedDate, setSelectedEventId, setScrollToEventId } =
    useWorkspaceStore();
  const [runningLateEvent, setRunningLateEvent] = useState(null);
  const pathnameRef = useRef(location.pathname);

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const openReminderMeeting = useCallback(
    (reminder) => {
      const selection = getReminderMeetingSelection(reminder.event);
      if (!selection) return;

      setSelectedDate(selection.selectedDate);
      setSelectedEventId(selection.eventId);
      hideNotification(reminder.id);

      if (pathnameRef.current !== "/workspace") {
        navigate("/workspace");
      }
    },
    [navigate, setSelectedDate, setSelectedEventId]
  );

  const requestReminderDateAndScroll = useCallback(
    (reminder) => {
      const selection = getReminderMeetingSelection(reminder.event);
      if (!selection?.eventId || pathnameRef.current !== "/workspace") return;

      setSelectedDate(selection.selectedDate);
      setScrollToEventId(selection.eventId);
    },
    [setScrollToEventId, setSelectedDate]
  );

  useEffect(() => {
    reminders.forEach((reminder) => {
      const theme = getReminderTheme(reminder.thresholdMinutes);
      requestReminderDateAndScroll(reminder);

      playMeetingReminderSound();
      showNotification({
        id: reminder.id,
        title: "Upcoming meeting",
        message: (
          <MeetingReminderMessage
            reminder={reminder}
            onRunningLate={setRunningLateEvent}
          />
        ),
        icon: <IconClock size={18} />,
        color: theme.color,
        autoClose: AUTO_DISMISS_MS,
        onClick: (event) => {
          if (isNotificationActionClick(event)) return;
          openReminderMeeting(reminder);
        },
        styles: {
          root: {
            borderTop: `4px solid ${theme.ribbonColor}`,
            cursor: "pointer",
          },
        },
      });

      dismissReminder(reminder.id);
    });
  }, [
    dismissReminder,
    openReminderMeeting,
    requestReminderDateAndScroll,
    reminders,
  ]);

  if (!enabled) return null;

  return (
    <>
      {runningLateEvent && (
        <RunningLateModal
          opened={!!runningLateEvent}
          setOpened={(opened) => {
            if (!opened) setRunningLateEvent(null);
          }}
          event={runningLateEvent}
        />
      )}
    </>
  );
};

export default UpcomingMeetingReminderHost;
